import React, { useState } from "react";
import { 
  CheckCircle2, 
  ShieldCheck, 
  X, 
  Loader2, 
  FileCheck, 
  QrCode, 
  Fingerprint, 
  Key, 
  Download, 
  Copy, 
  Check, 
  ExternalLink,
  Sparkles
} from "lucide-react";

import { Language } from "../utils/i18n";

interface DigitalSignatureModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentTitle: string;
  documentContent: string;
  onSignatureComplete?: (stampText: string, receipt: any) => void;
  language?: Language;
}

export const DigitalSignatureModal: React.FC<DigitalSignatureModalProps> = ({
  isOpen,
  onClose,
  documentTitle,
  documentContent,
  onSignatureComplete,
  language = 'uk'
}) => {
  const isPl = language === 'pl';
  const isEn = language === 'en';
  const isDe = language === 'de';
  const isFr = language === 'fr';
  const isUk = language === 'uk' || (!isPl && !isEn && !isDe && !isFr);

  const [provider, setProvider] = useState<"diia" | "vchasno" | "csk" | "eidas">("diia");
  const [signerName, setSignerName] = useState("");
  const [signerTaxId, setSignerTaxId] = useState("");
  const [isSigning, setIsSigning] = useState(false);
  const [signedResult, setSignedResult] = useState<any>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const handleSign = async () => {
    setIsSigning(true);
    setError("");
    try {
      const res = await fetch("/api/digital-signature/sign", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          documentTitle,
          documentContent,
          provider,
          signerName: signerName.trim() || undefined,
          signerTaxId: signerTaxId.trim() || undefined
        })
      });

      const data = await res.json();
      if (res.ok && data.status === "success") {
        setSignedResult(data);
        if (onSignatureComplete) {
          onSignatureComplete(data.stampText, data.receipt);
        }
      } else {
        setError(data.error || (isPl ? "Błąd podczas generowania podpisu cyfrowego." : isEn ? "Error generating digital signature." : "Помилка при формуванні цифрового підпису."));
      }
    } catch (err: any) {
      setError(isPl ? "Błąd połączenia z serwerem podpisu." : isEn ? "Connection error to signing server." : "Помилка з'єднання з сервером підписання.");
    } finally {
      setIsSigning(false);
    }
  };

  const handleCopyStamp = () => {
    if (signedResult?.stampText) {
      navigator.clipboard.writeText(signedResult.stampText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownloadReceipt = () => {
    if (!signedResult) return;
    const blob = new Blob([JSON.stringify(signedResult.receipt, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `signature_receipt_${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-zinc-900 border border-zinc-700/80 rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl flex flex-col max-h-[92vh]">
        
        {/* Modal Header */}
        <div className="p-5 bg-gradient-to-r from-emerald-950/60 via-zinc-900 to-zinc-900 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                {isPl ? 'Składanie Podpisu Kwalifikowanego / Elektronicznego' : isEn ? 'Apply QES / Digital Signature' : 'Накладання КЕП / Електронного Підпису'}
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 font-mono font-medium border border-emerald-500/30">
                  {isPl ? 'eIDAS & Dz.U. 2016 poz. 1579' : isEn ? 'eIDAS & Regulation (EU) 910/2014' : 'ЗУ № 2155-VIII & eIDAS'}
                </span>
              </h3>
              <p className="text-xs text-zinc-400 truncate max-w-md">
                {isPl ? 'Dokument: ' : isEn ? 'Document: ' : 'Документ: '}<span className="text-zinc-200 font-medium">{documentTitle}</span>
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto space-y-5 text-sm">
          {!signedResult ? (
            <>
              {/* Provider Selection */}
              <div>
                <label className="block text-xs font-semibold text-zinc-300 mb-2 uppercase tracking-wider">
                  {isPl ? '1. Wybierz dostawcę podpisu elektronicznego:' : isEn ? '1. Select Digital Signature Provider:' : '1. Оберіть провайдера електронного підпису:'}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setProvider("diia")}
                    className={`p-3.5 rounded-xl border text-left transition-all flex items-start gap-3 ${
                      provider === "diia"
                        ? "bg-emerald-950/40 border-emerald-500/60 text-white shadow-lg shadow-emerald-950/40"
                        : "bg-zinc-800/40 border-zinc-800 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
                      <QrCode className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="font-bold text-white text-xs">
                        {isPl ? 'Diia.Podpis (QR Biometria)' : isEn ? 'Diia.Sign (Biometric QES)' : 'Дія.Підпис (Мінцифри)'}
                      </div>
                      <div className="text-[11px] text-zinc-400 mt-0.5">
                        {isPl ? 'Szybka weryfikacja biometryczna smartfonem' : isEn ? 'Fast biometric mobile phone verification' : 'Швидка біометрична верифікація через смартфон'}
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setProvider("vchasno")}
                    className={`p-3.5 rounded-xl border text-left transition-all flex items-start gap-3 ${
                      provider === "vchasno"
                        ? "bg-blue-950/40 border-blue-500/60 text-white shadow-lg shadow-blue-950/40"
                        : "bg-zinc-800/40 border-zinc-800 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-blue-500/20 text-blue-400 flex items-center justify-center shrink-0">
                      <Fingerprint className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="font-bold text-white text-xs">
                        {isPl ? 'Chmurowy Podpis Kwalifikowany' : isEn ? 'Cloud QES / Corporate E-Sign' : 'Вчасно.КЕП / Хмарний КЕП'}
                      </div>
                      <div className="text-[11px] text-zinc-400 mt-0.5">
                        {isPl ? 'Integracja z elektronicznym obiegiem umów' : isEn ? 'Integration with corporate document workflows' : 'Інтеграція з корпоративним документообігом'}
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setProvider("csk")}
                    className={`p-3.5 rounded-xl border text-left transition-all flex items-start gap-3 ${
                      provider === "csk"
                        ? "bg-amber-950/40 border-amber-500/60 text-white shadow-lg shadow-amber-950/40"
                        : "bg-zinc-800/40 border-zinc-800 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0">
                      <Key className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="font-bold text-white text-xs">
                        {isPl ? 'Akredytowane Centrum (KIR / Certum / Smart-ID)' : isEn ? 'Accredited QTSP / Hardware Token' : 'Акредитований ЦСК / КЕП'}
                      </div>
                      <div className="text-[11px] text-zinc-400 mt-0.5">
                        {isPl ? 'Szafir, EuroCert, Certum, Smart-ID' : isEn ? 'QTSP, Smart-ID, USB Token, X.509 DSTU' : 'ДПС, ПриватБанк, Smart-ID, ДСТУ 4145-2002'}
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setProvider("eidas")}
                    className={`p-3.5 rounded-xl border text-left transition-all flex items-start gap-3 ${
                      provider === "eidas"
                        ? "bg-indigo-950/40 border-indigo-500/60 text-white shadow-lg shadow-indigo-950/40"
                        : "bg-zinc-800/40 border-zinc-800 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-indigo-500/20 text-indigo-400 flex items-center justify-center shrink-0">
                      <Sparkles className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="font-bold text-white text-xs">
                        {isPl ? 'eIDAS QES (Polska / UE / mObywatel)' : isEn ? 'eIDAS QES (EU Cross-Border)' : 'eIDAS QES (EU / Польща)'}
                      </div>
                      <div className="text-[11px] text-zinc-400 mt-0.5">
                        {isPl ? 'Podpis kwalifikowany ważny w całej UE (mObywatel/KIR)' : isEn ? 'Qualified electronic signature valid in all 27 EU states' : 'Кваліфікований підпис для угод у ЄС (mObywatel/KIR)'}
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Signer Data */}
              <div className="space-y-3">
                <label className="block text-xs font-semibold text-zinc-300 mb-2 uppercase tracking-wider">
                  {isPl ? '2. Dane osoby podpisującej:' : isEn ? '2. Signer Details:' : '2. Реквізити підписанта:'}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-[11px] text-zinc-400 block mb-1">
                      {isPl ? 'Imię i nazwisko osoby reprezentującej:' : isEn ? 'Signer full name / CEO:' : 'ПІБ керівника / підписанта:'}
                    </span>
                    <input 
                      type="text" 
                      placeholder={isPl ? "np. Jan Kowalski" : isEn ? "e.g. John Doe" : "наприклад, Іваненко І. В."}
                      value={signerName}
                      onChange={(e) => setSignerName(e.target.value)}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <span className="text-[11px] text-zinc-400 block mb-1">
                      {isPl ? 'Numer NIP / PESEL / KRS:' : isEn ? 'Tax ID / NIP / EDRPOU:' : 'ЄДРПОУ / РНОКПП / NIP:'}
                    </span>
                    <input 
                      type="text" 
                      placeholder={isPl ? "np. PL1234567890" : isEn ? "e.g. 24507442 or US12-3456789" : "наприклад, 24507442"}
                      value={signerTaxId}
                      onChange={(e) => setSignerTaxId(e.target.value)}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                </div>
              </div>

              {/* Legal Notice */}
              <div className="bg-emerald-950/20 border border-emerald-500/20 rounded-xl p-3.5 text-xs text-emerald-200/90 flex items-start gap-2.5">
                <FileCheck className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold text-white mb-0.5">
                    {isPl ? 'Gwarancja ważności prawnej' : isEn ? 'Legal Validity Guarantee' : 'Гарантія юридичної дійсності'}
                  </div>
                  {isPl 
                    ? 'Podpis tworzy niezmienny skrót kryptograficzny SHA-256 z urzędowym znacznikiem czasu RFC 3161 TSP i integruje oficjalną pieczęć weryfikacyjną.' 
                    : isEn 
                    ? 'Signature generates an immutable SHA-256 cryptographic document hash with RFC 3161 TSP timestamp and embeds an official verification stamp.' 
                    : 'Підпис формує незмінний криптографічний хеш документа SHA-256 з міткою точного часу RFC 3161 TSP та інтегрує офіційний штамп підпису для контрагентів.'}
                </div>
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-red-950/40 border border-red-500/30 text-red-300 text-xs">
                  {error}
                </div>
              )}
            </>
          ) : (
            /* Signed Success View */
            <div className="space-y-4 animate-in zoom-in-95 duration-200">
              <div className="bg-emerald-950/30 border border-emerald-500/40 rounded-xl p-4 text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 flex items-center justify-center mx-auto mb-2.5">
                  <CheckCircle2 className="w-7 h-7" />
                </div>
                <h4 className="text-base font-bold text-white">
                  {isPl ? 'Dokument został pomyślnie podpisany podpisem kwalifikowanym!' : isEn ? 'Document successfully signed with QES!' : 'Документ успішно підписано КЕП!'}
                </h4>
                <p className="text-xs text-emerald-300 mt-1">
                  {isPl 
                    ? 'Wygenerowano pieczęć kryptograficzną i poświadczenie odbioru zgodnie ze standardem eIDAS / Dz.U.' 
                    : isEn 
                    ? 'Cryptographic verification stamp and receipt generated compliant with eIDAS regulation.' 
                    : 'Сформовано криптографічний штамп та квитанцію перевірки за стандартом eIDAS / ЗУ № 2155-VIII.'}
                </p>
              </div>

              {/* Stamp Visual Preview */}
              <div>
                <label className="block text-xs font-semibold text-zinc-300 mb-1.5">
                  {isPl ? 'Pieczęć graficzna podpisu elektronicznego:' : isEn ? 'Visual Electronic Signature Stamp:' : 'Графічний штамп електронного підпису:'}
                </label>
                <div 
                  className="bg-slate-900 border border-emerald-500/40 rounded-xl p-3 overflow-x-auto"
                  dangerouslySetInnerHTML={{ __html: signedResult.stampSvg }}
                />
              </div>

              {/* Text Stamp Details */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-zinc-300">
                    {isPl ? 'Klauzula tekstowa do wklejenia w umowie:' : isEn ? 'Text Signature Clause for Contract:' : 'Текстовий реквізит для вставки в договір:'}
                  </label>
                  <button 
                    onClick={handleCopyStamp}
                    className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1 font-medium"
                  >
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? (isPl ? "Skopiowano!" : isEn ? "Copied!" : "Скопійовано!") : (isPl ? "Skopiuj" : isEn ? "Copy" : "Скопіювати")}
                  </button>
                </div>
                <pre className="bg-zinc-950 border border-zinc-800 rounded-xl p-3 text-[10px] font-mono text-zinc-300 overflow-x-auto whitespace-pre">
                  {signedResult.stampText}
                </pre>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-4 bg-zinc-950 border-t border-zinc-800 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-xs font-medium text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
          >
            {signedResult ? (isPl ? "Zamknij" : isEn ? "Close" : "Закрити") : (isPl ? "Anuluj" : isEn ? "Cancel" : "Скасувати")}
          </button>

          {!signedResult ? (
            <button
              onClick={handleSign}
              disabled={isSigning}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs flex items-center gap-2 shadow-lg shadow-emerald-950/50 disabled:opacity-50 transition-all"
            >
              {isSigning ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {isPl ? 'Podpisywanie...' : isEn ? 'Signing...' : 'Підписання КЕП...'}
                </>
              ) : (
                <>
                  <ShieldCheck className="w-4 h-4" />
                  {isPl ? 'Złóż Podpis Kwalifikowany' : isEn ? 'Apply Digital Signature / QES' : 'Накласти ЕЦП / Підписати'}
                </>
              )}
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={handleDownloadReceipt}
                className="px-4 py-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-200 font-semibold text-xs flex items-center gap-1.5 transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
                {isPl ? 'Potwierdzenie (.json)' : isEn ? 'Receipt (.json)' : 'Квитанція (.json)'}
              </button>
              <button
                onClick={onClose}
                className="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center gap-1.5 transition-colors"
              >
                <Check className="w-4 h-4" />
                {isPl ? 'Gotowe' : isEn ? 'Done' : 'Готово'}
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};
